import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Typography, Box, Link, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import colors from '../../design-system/colors';
import spacing from '../../design-system/spacing';
import typography from '../../design-system/typography';

// Builds the markdown -> MUI component map using the *active* theme so chat
// messages (rendered from AI/agent markdown) always use theme.palette text
// and surface colors. Previously these were hardcoded light-mode hex values,
// which produced illegible dark-on-dark (or white-on-white) text whenever the
// chat bubble surface adapted to dark mode but the markdown content did not.
const getMarkdownComponents = (theme) => ({
  p: ({ children }) => (
    <Typography
      variant="body2"
      sx={{
        mb: 1,
        lineHeight: typography.lineHeight.normal,
        color: 'text.primary',
        '&:last-child': { mb: 0 },
      }}
    >
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: typography.fontWeight.bold, color: 'text.primary' }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: typography.fontWeight.bold, color: 'text.primary' }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5, fontWeight: typography.fontWeight.semibold, color: 'text.primary' }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: typography.fontWeight.semibold, color: 'text.primary' }}>
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: typography.fontWeight.bold }}>
      {children}
    </Box>
  ),
  em: ({ children }) => (
    <Box component="em" sx={{ fontStyle: 'italic' }}>
      {children}
    </Box>
  ),
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
      {children}
    </Link>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 2.5, mb: 1, '& li': { mb: 0.3 } }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 2.5, mb: 1, '& li': { mb: 0.3 } }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body2" sx={{ color: 'text.primary', lineHeight: typography.lineHeight.normal }}>
      {children}
    </Typography>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <Box
        component="code"
        sx={{
          backgroundColor: 'action.hover',
          color: theme.palette.mode === 'dark' ? colors.primary[200] : colors.primary[800],
          px: 0.6,
          py: 0.2,
          borderRadius: spacing.borderRadius.sm,
          fontSize: typography.fontSize.xs,
          fontFamily: typography.fontFamily.mono,
        }}
      >
        {children}
      </Box>
    ) : (
      <Box
        component="pre"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? colors.neutral.black : colors.neutral[900],
          color: colors.neutral[100],
          p: 1.5,
          borderRadius: spacing.borderRadius.md,
          overflow: 'auto',
          fontSize: typography.fontSize.xs,
          fontFamily: typography.fontFamily.mono,
          my: 1,
          '& code': { color: 'inherit', backgroundColor: 'transparent', p: 0 },
        }}
      >
        <code>{children}</code>
      </Box>
    ),
  blockquote: ({ children }) => (
    <Box
      sx={{
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        pl: 2,
        py: 0.5,
        my: 1,
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
        color: 'text.primary',
        borderRadius: `0 ${spacing.borderRadius.sm}px ${spacing.borderRadius.sm}px 0`,
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 1 }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: typography.fontSize.xs,
          color: 'text.primary',
          '& th': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            fontWeight: typography.fontWeight.bold,
            p: 1,
            textAlign: 'left',
            borderBottom: `2px solid ${theme.palette.primary.dark}`,
          },
          '& td': {
            p: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
          '& tr:nth-of-type(even)': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{ border: 'none', borderTop: '1px solid', borderColor: 'divider', my: 1.5 }}
    />
  ),
});

const TextRenderer = ({ content }) => {
  const theme = useTheme();
  if (!content) return null;

  return (
    <Box sx={{ '& > *:first-of-type': { mt: 0 } }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={getMarkdownComponents(theme)}>
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default TextRenderer;
